import { Injectable, NgZone, inject } from '@angular/core';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Auth } from './auth';

export interface BatchProgressPayload {
  batchId: string;
  total: number | null;
  processed: number | null;
  failed: number | null;
  percentComplete: number | null;
}

export interface BatchStatusPayload {
  batchId: string;
  status: string;
  completedAt?: string | null;
}

export interface BatchCreationCompletedPayload {
  batchId: string;
  status: string;
  totalDocuments: number | null;
}

export interface BatchCreationFailedPayload {
  batchId: string;
  status: string;
  errorMessage: string | null;
}

export interface DocumentStatusPayload {
  batchId: string;
  documentId: string;
  status: string;
  errorMessage: string | null;
}

export interface RepoIngestionStartedPayload {
  connectionId: string;
  repoName: string;
}

export interface RepoIngestionCompletedPayload {
  connectionId: string;
  repoName: string;
  success: boolean;
  entityCount: number | null;
  relationshipCount: number | null;
  error: string | null;
}

/** Origin-absolute hub URL so deployed /engine/ base href cannot rewrite it. */
function resolveHubUrl(): string {
  const path = environment.apiUrl.replace(/\/api\/?$/, '') + '/hubs/ingestion';
  if (/^https?:\/\//i.test(path)) return path;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (typeof window === 'undefined') return normalized;
  return `${window.location.origin}${normalized}`;
}

/**
 * One shared SignalR connection to /hubs/ingestion, reused across pages
 * instead of each page building its own HubConnection. Consumers join
 * whichever batch groups (or the fixed code-graph group) they care about
 * and subscribe to the typed observables below.
 */
@Injectable({ providedIn: 'root' })
export class IngestionRealtime {
  private readonly authService = inject(Auth);
  private readonly ngZone = inject(NgZone);
  private readonly hubUrl = resolveHubUrl();
  private connection?: HubConnection;
  private connectPromise?: Promise<void>;
  /** Lowercased batch ids → number of pages currently watching. */
  private readonly batchJoinCounts = new Map<string, number>();
  private joinedCodeGraphGroup = false;

  private readonly batchProgress = new Subject<BatchProgressPayload>();
  private readonly batchStarted = new Subject<BatchStatusPayload>();
  private readonly batchCompleted = new Subject<BatchStatusPayload>();
  private readonly batchCreationCompleted = new Subject<BatchCreationCompletedPayload>();
  private readonly batchCreationFailed = new Subject<BatchCreationFailedPayload>();
  private readonly documentStatusChanged = new Subject<DocumentStatusPayload>();
  private readonly repoIngestionStarted = new Subject<RepoIngestionStartedPayload>();
  private readonly repoIngestionCompleted = new Subject<RepoIngestionCompletedPayload>();

  readonly batchProgress$ = this.batchProgress.asObservable();
  readonly batchStarted$ = this.batchStarted.asObservable();
  readonly batchCompleted$ = this.batchCompleted.asObservable();
  readonly batchCreationCompleted$ = this.batchCreationCompleted.asObservable();
  readonly batchCreationFailed$ = this.batchCreationFailed.asObservable();
  readonly documentStatusChanged$ = this.documentStatusChanged.asObservable();
  readonly repoIngestionStarted$ = this.repoIngestionStarted.asObservable();
  readonly repoIngestionCompleted$ = this.repoIngestionCompleted.asObservable();

  /** Ensures the connection exists and is started; safe to call repeatedly. */
  connect(): Promise<void> {
    if (this.connectPromise) return this.connectPromise;

    this.connection = new HubConnectionBuilder()
      .withUrl(this.hubUrl, { accessTokenFactory: () => this.authService.getAccessToken() ?? '' })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    this.registerHandlers();

    this.connection.onreconnected(() => {
      void this.rejoinGroups();
    });

    this.connection.onclose(() => {
      this.connectPromise = undefined;
    });

    this.connectPromise = this.connection.start().catch(err => {
      console.error('[IngestionRealtime] Failed to start connection:', err);
      this.connectPromise = undefined;
      throw err;
    });

    return this.connectPromise;
  }

  async joinBatch(batchId: string): Promise<void> {
    const id = batchId.toLowerCase();
    this.batchJoinCounts.set(id, (this.batchJoinCounts.get(id) ?? 0) + 1);
    await this.connect();
    // Always re-invoke after connect. Capturing count before await races when
    // two pages join the same batch while the socket is still starting, and
    // both skip JoinBatch — then progress events never arrive.
    if (this.connection?.state === 'Connected') {
      await this.connection.invoke('JoinBatch', id).catch(err =>
        console.error(`[IngestionRealtime] Failed to join batch ${id}:`, err));
    }
  }

  leaveBatch(batchId: string): void {
    const id = batchId.toLowerCase();
    const count = (this.batchJoinCounts.get(id) ?? 0) - 1;
    if (count > 0) {
      this.batchJoinCounts.set(id, count);
      return;
    }
    this.batchJoinCounts.delete(id);
    void this.connection?.invoke('LeaveBatch', id).catch(() => {});
  }

  async joinCodeGraphGroup(): Promise<void> {
    this.joinedCodeGraphGroup = true;
    await this.connect();
    if (this.connection?.state === 'Connected') {
      await this.connection.invoke('JoinGroup', 'code-graph').catch(err =>
        console.error('[IngestionRealtime] Failed to join code-graph group:', err));
    }
  }

  private async rejoinGroups(): Promise<void> {
    for (const batchId of this.batchJoinCounts.keys()) {
      await this.connection?.invoke('JoinBatch', batchId).catch(() => {});
    }
    if (this.joinedCodeGraphGroup) {
      await this.connection?.invoke('JoinGroup', 'code-graph').catch(() => {});
    }
  }

  private registerHandlers(): void {
    if (!this.connection) return;

    this.connection.on('BatchProgressUpdated', (payload: any) => {
      this.ngZone.run(() => {
        const batchId = this.readValue(payload, 'BatchId');
        if (!batchId) return;
        this.batchProgress.next({
          batchId: String(batchId),
          total: this.toNumber(this.readValue(payload, 'Total')),
          processed: this.toNumber(this.readValue(payload, 'Processed')),
          failed: this.toNumber(this.readValue(payload, 'Failed')),
          percentComplete: this.toNumber(this.readValue(payload, 'PercentComplete'))
        });
      });
    });

    this.connection.on('BatchIngestionStarted', (payload: any) => {
      this.ngZone.run(() => {
        const batchId = this.readValue(payload, 'BatchId');
        if (!batchId) return;
        this.batchStarted.next({ batchId: String(batchId), status: this.readValue(payload, 'Status') });
      });
    });

    this.connection.on('BatchCompleted', (payload: any) => {
      this.ngZone.run(() => {
        const batchId = this.readValue(payload, 'BatchId');
        if (!batchId) return;
        this.batchCompleted.next({
          batchId: String(batchId),
          status: this.readValue(payload, 'Status'),
          completedAt: this.readValue(payload, 'CompletedAt')
        });
      });
    });

    this.connection.on('BatchCreationCompleted', (payload: any) => {
      this.ngZone.run(() => {
        const batchId = this.readValue(payload, 'BatchId');
        if (!batchId) return;
        this.batchCreationCompleted.next({
          batchId: String(batchId),
          status: this.readValue(payload, 'Status'),
          totalDocuments: this.toNumber(this.readValue(payload, 'TotalDocuments'))
        });
      });
    });

    this.connection.on('BatchCreationFailed', (payload: any) => {
      this.ngZone.run(() => {
        const batchId = this.readValue(payload, 'BatchId');
        if (!batchId) return;
        this.batchCreationFailed.next({
          batchId: String(batchId),
          status: this.readValue(payload, 'Status'),
          errorMessage: this.readValue(payload, 'ErrorMessage')
        });
      });
    });

    this.connection.on('DocumentStatusChanged', (payload: any) => {
      this.ngZone.run(() => {
        const batchId = this.readValue(payload, 'BatchId');
        const documentId = this.readValue(payload, 'DocumentId');
        if (!batchId || !documentId) return;
        this.documentStatusChanged.next({
          batchId: String(batchId),
          documentId: String(documentId),
          status: this.readValue(payload, 'Status'),
          errorMessage: this.readValue(payload, 'ErrorMessage')
        });
      });
    });

    this.connection.on('RepoIngestionStarted', (payload: any) => {
      this.ngZone.run(() => {
        const repoName = this.readValue(payload, 'RepoName');
        if (!repoName) return;
        this.repoIngestionStarted.next({
          connectionId: String(this.readValue(payload, 'ConnectionId') ?? ''),
          repoName
        });
      });
    });

    this.connection.on('RepoIngestionCompleted', (payload: any) => {
      this.ngZone.run(() => {
        const repoName = this.readValue(payload, 'RepoName');
        if (!repoName) return;
        this.repoIngestionCompleted.next({
          connectionId: String(this.readValue(payload, 'ConnectionId') ?? ''),
          repoName,
          success: !!this.readValue(payload, 'Success'),
          entityCount: this.toNumber(this.readValue(payload, 'EntityCount')),
          relationshipCount: this.toNumber(this.readValue(payload, 'RelationshipCount')),
          error: this.readValue(payload, 'Error')
        });
      });
    });
  }

  private readValue(payload: any, key: string): any {
    if (!payload) return null;
    return payload[key] ?? payload[key.charAt(0).toLowerCase() + key.slice(1)] ?? null;
  }

  private toNumber(value: unknown): number | null {
    if (typeof value === 'number' && !Number.isNaN(value)) return value;
    if (typeof value === 'string' && value.trim() !== '') {
      const n = Number(value);
      return Number.isNaN(n) ? null : n;
    }
    return null;
  }
}
